import React, { useState } from 'react';
import { Bell, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'components/common/Card';
import { EstimatedAmount } from 'components/common/EstimatedAmount';
import { useStore } from 'context/StoreContext';
import { useApi } from 'hooks/useApi';
import { AlertService } from 'services/alerts/alert.service';

export function NotificationBell() {
  const { org, currentStore } = useStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data } = useApi(
    () => org && currentStore
      ? AlertService.list(org.id, currentStore.id, 'NEW', 1, 5)
      : Promise.resolve(null),
    [org?.id, currentStore?.id]
  );
  const alerts = data?.data ?? [];
  const total = data?.meta.total ?? alerts.length;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(value => !value)}
        className="relative rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        aria-label={total > 0 ? `${total} new alerts` : 'No new alerts'}
      >
        <Bell size={18} />
        {total > 0 && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-500" />}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-40 mt-2 w-80 max-w-[calc(100vw-2rem)]">
          <Card padding={false}>
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-900">New alerts</h3>
              <span className="text-xs text-gray-400">{total} new</span>
            </div>
            {alerts.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-gray-500">You're all caught up.</p>
            ) : (
              <div className="divide-y divide-gray-100">
                {alerts.map(alert => (
                  <button
                    key={alert.id}
                    onClick={() => { setOpen(false); navigate('/alerts'); }}
                    className="flex w-full gap-3 px-4 py-3 text-left hover:bg-gray-50"
                  >
                    <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-orange-500" />
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium text-gray-900">{alert.title}</p>
                      {alert.estimated_monthly_impact != null && (
                        <EstimatedAmount amount={alert.estimated_monthly_impact} className="mt-0.5 text-xs" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={() => { setOpen(false); navigate('/alerts'); }}
              className="w-full border-t border-gray-100 px-4 py-2 text-xs font-medium text-blue-600 hover:bg-gray-50"
            >
              View all alerts
            </button>
          </Card>
        </div>
      )}
    </div>
  );
}
